import * as service from "../../service";
import React, { FC, memo, useCallback, useMemo, useState } from "react";
import { Overlay } from "../../components";
import { LANGUAGE } from "_constants";
import { Carousel } from "react-responsive-carousel";
import AdDisplay from "components/common/AdDisplay";
import AdBannerSvg from "./assets/ad-banner.svg";
import GeniusFeeds from "../../assets/ads/genius-logo.svg";
import FastGamesApi from "../../assets/ads/fastgamesapi.svg";
import Monetria from "../../assets/ads/monetria.svg";
import { SportTabs } from "components/common/SportTabs";
import { TennisStatusRenderer } from "./components/StatusRenderer";
import PreMatchIntro from "../../components/PreMatchIntro";
import { TENNIS_TABS, TennisTabKey, processStatus } from "./utils";
import { MESSAGES } from "./constants";
import { Timer } from "./components/Timer";
import { Ball } from "./components/Ball";
import { Stats } from "./statistics/index";
import { ServiceIndicator } from "./indicators/ServiceIndicator";
import TennisArena from "./assets/tennis-bg.png";
import TennisCourt from "./assets/tennis-court.svg";
import {
  AdBanner,
  FlatLayer,
  FullHeightTabPanel,
  createBackgroundComponent,
} from "../../components/common";

export type TennisProps = {
  status: service.MatchStatus;
  language?: LANGUAGE;
};

const StyledCourtTennis = createBackgroundComponent({
  backgroundImage: TennisArena,
  baseClassName:
    "w-full h-full bg-center bg-cover flex items-center justify-center relative overflow-hidden",
  displayName: "StyledCourtTennis",
});

const Court = createBackgroundComponent({
  backgroundImage: TennisCourt,
  baseClassName: "absolute inset-0 w-full h-full bg-center bg-cover",
  displayName: "TennisCourt",
});

export const Tennis: FC<TennisProps> = memo(
  ({ status, language = LANGUAGE.ENGLISH }) => {
    const [activeTab, setActiveTab] = useState<TennisTabKey>("narration");
    const tabs = useMemo(() => TENNIS_TABS, []);

    const { normalizedStatusCode } = useMemo(
      () => processStatus(status, language),
      [status, language]
    );

    const messageGenerator = useCallback(
      ({
        normalizedStatusCode,
        statusMessage,
      }: {
        normalizedStatusCode?: string;
        statusMessage?: string;
      }) => {
        if (!normalizedStatusCode) return undefined;
        const langDict = MESSAGES[language];
        const lookup = langDict?.[normalizedStatusCode];
        const resolvedMessage = lookup ?? statusMessage;
        if (!resolvedMessage) return undefined;
        const upper =
          typeof resolvedMessage === "string"
            ? resolvedMessage.toUpperCase()
            : resolvedMessage;
        return { message: upper };
      },
      [language]
    );

    const isPreMatch = useMemo(() => {
      const stsVal = status?.sts as unknown;
      const stsNum =
        typeof stsVal === "number"
          ? stsVal
          : typeof stsVal === "string"
          ? Number(stsVal)
          : NaN;
      const notStartedByStatus = !Number.isNaN(stsNum) && stsNum === 0;

      const tmVal = status?.tm as unknown;
      const tmNum =
        typeof tmVal === "number"
          ? tmVal
          : typeof tmVal === "string"
          ? Number(tmVal)
          : NaN;
      const notStartedByTime = !Number.isNaN(tmNum) && tmNum <= 0;

      const noLiveNsc = !normalizedStatusCode;
      return (notStartedByStatus || notStartedByTime) && noLiveNsc;
    }, [status, normalizedStatusCode]);

    const selectedTabIndex = useMemo(() => {
      const idx = tabs.findIndex((tab) => tab.key === activeTab);
      return idx >= 0 ? idx : 0;
    }, [activeTab, tabs]);

    return (
      <>
        <SportTabs
          tabs={tabs}
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as TennisTabKey)}
        />

        <Carousel
          showThumbs={false}
          selectedItem={selectedTabIndex}
          autoPlay={false}
          emulateTouch
          showStatus={false}
          showArrows={false}
          showIndicators={false}
          swipeable={false}
        >
          {tabs.map((tab: { key: TennisTabKey; label: string }) => {
            if (tab.key === "narration") {
              return (
                <StyledCourtTennis className="styled-court" key="narration">
                  <Timer status={status} />
                  <AdBanner src={AdBannerSvg} alt="Ad Banner" />
                  <AdDisplay adImages={[GeniusFeeds, FastGamesApi, Monetria]} />
                  <Overlay
                    status={status}
                    messageGenerator={messageGenerator}
                    language={language}
                    StatusRenderer={TennisStatusRenderer}
                  >
                    <Court />

                    {!isPreMatch && (
                      <>
                        <ServiceIndicator
                          status={status}
                          normalizedCode={normalizedStatusCode}
                        />
                      </>
                    )}
                  </Overlay>

                  <FlatLayer>
                    {!isPreMatch && (
                      <>
                        <Ball status={status} />
                      </>
                    )}
                  </FlatLayer>

                  {isPreMatch && (
                    <FlatLayer>
                      <PreMatchIntro status={status} />
                    </FlatLayer>
                  )}
                </StyledCourtTennis>
              );
            }

            if (tab.key === "stats") {
              return (
                <FullHeightTabPanel key="stats">
                  <Stats status={status} />
                </FullHeightTabPanel>
              );
            }

            return null;
          })}
        </Carousel>
      </>
    );
  }
);

export { StyledCourtTennis, Overlay };
